import {get, isEmpty, isNil, orderBy, sortBy} from 'lodash'
import uuid from 'helpers/uuid'
import {featureIsAllocatable, features, permissions, USER_LEVEL} from 'helpers/constants'
import {getOrderedPackages, licenceOrder} from 'helpers/contacts'
import {userHasPermission} from 'helpers/user'
import {
  capAccountPackageQuantity,
  createAccountPackage,
  disableNonEntitledAdditionalPackages,
  findAccountPackageForFeaturePackage,
  findPackageIdByPartCode,
  findPackagePartCode,
  getNonMainAccountPackages,
  getNonTrialAccountPackages,
  getSelectedMainAccountPackage,
  getSelectedTrialAccountPackage,
  isDeliverySchedulingPackage,
  mainPackages,
  packagesShownWithLicences,
  partCodes,
  updateAccountPackageQuantity,
} from 'helpers/plans'

export const getUsersFromContacts = (contacts) => {
  return Object.values(contacts)
      .map(contact => {
        const {id, userId, ...otherProps} = contact
        return {
          id: userId,
          contactId: id,
          ...otherProps,
        }
      })
}

export const applyDefaultLicences = (users, licences) => {
  return users.map(user => {
    if (user.level === USER_LEVEL.MASTER && user.activeLicences.length < 2) {
      const defaultLicences = getDefaultLicencesForNewUser(licences, users)
      const activeLicences = addLicencesToUser(user, defaultLicences)

      return {
        ...user,
        activeLicences,
      }
    }

    return user
  })
}

export const sortUsers = (currentUser, users) => {
  const sortedUsers = orderBy(users.filter(user => user.id !== currentUser.id), ['name'])
  if (currentUser) {
    const thisUser = users.find(user => user.id === currentUser.id)
    if (thisUser) sortedUsers.unshift(thisUser)
  }
  return sortedUsers
}

export const getContactsThatAreNotUsers = (contacts, users) => {
  const userContactIds = users.map(user => user.contactId)
  return contacts.filter(contact => !userContactIds.includes(contact.id))
}

export const sortContacts = (contacts) => {
  return orderBy(contacts, 'name')
}

export const canContactBeAddedAsUser = (contact) => {
  return isNil(getContactNotUserLabel(contact))
}

export const getContactLabel = (contact) => {
  let label = contact.name
  const badEmailLabel = getContactNotUserLabel(contact)

  if (!isEmpty(contact.email)) label += ` (${contact.email})`
  if (badEmailLabel) label += ` [${badEmailLabel}]`

  return label
}

export const getContactNotUserLabel = (contact) => {
  if (contact.hasMasterLicence) return 'already a user'
  if (isEmpty(contact.email)) return 'no email address'
  if (!contact.isEmailValid) return 'invalid email address'
  if (contact.isEmailInUse) return 'email address already in use'
  return null
}

export const createUserFromContact = (contact) => {
  return {
    name: contact.name,
    email: contact.email,
    id: uuid(),
    contactId: contact.id,
    roleId: 0,
    isNew: true,
    hasMasterLicence: true,
    activeLicences: [],
    activePackages: [],
  }
}

export const createUserFromEmail = (email) => {
  return {
    email,
    id: uuid(),
    roleId: 0,
    isNew: true,
    hasMasterLicence: true,
    activeLicences: [],
    activePackages: [],
  }
}

export const getDefaultLicencesForNewUser = (licences, users) => {
  return [
    {id: features.MASTER_LICENCE},
    ...getAvailableLicences(licences).filter(licence => isLicenceAvailableToAllocate(licence, users)),
  ]
}

export const resetUserLicences = (userToReset, allUsers) => {
  return allUsers.map(user => {
    return user.id === userToReset.id
        ? resetLicencesForUser(user)
        : user
  })
}

export const resetLicencesForUser = (userToReset) => {
  return {
    ...userToReset,
    activeLicences: [],
    activePackages: [],
  }
}

export const isAdminRole = role => role.id === -1

export const isUserRole = role => role.id === 0

export const getAdminRole = (roles) => roles.find(isAdminRole)

export const getUserRole = (roles) => roles.find(isUserRole)

export const sortRoles = (roles) => {
  const adminRole = getAdminRole(roles)
  const userRole = getUserRole(roles)

  return [
    adminRole,
    userRole,
    ...orderBy(
        roles.filter(role => !isAdminRole(role) && !isUserRole(role)),
        ['name']
    ),
  ]
}

export const findRole = (roles, roleId) => {
  return roles.find(role => role.id === roleId)
}

export const getAvailableLicences = (licences) => {
  return licences.filter(licence => licence.id !== features.MASTER_LICENCE && licence.qty > 0)
}

export const getUserLicences = (user) => {
  return user.activeLicences || []
}

export const getUserPackages = (user) => {
  return user?.activePackages || []
}

export const isLicenceAllocatedToUser = (user, licence) => {
  return !isNil(licence)
      && getUserLicences(user).map(licence => licence.id).includes(licence.id || licence.licenceId)
}

export const isLicenceCheckboxChecked = (user, licence) => {
  const userLicences = getUserLicences(user)
  return !isNil(licence)
      && (userLicences.map(userLicence => userLicence.id).includes(licence.id || licence.licenceId)
          || userLicences.map(userLicence => !isNil(userLicence.licenceId) && userLicence.licenceId)
              .includes(licence.licenceId))
}

export const isPackageAllocatedToUser = (user, userPackage) => {
  return !isNil(userPackage)
      && getUserPackages(user)
          .map(userPackage => userPackage.packageId || userPackage.id)
          .includes(userPackage.id || userPackage.packageId)
}

export const getAllocatedUsersForLicence = (licence, users) => {
  return users.filter(user => isLicenceAllocatedToUser(user, licence))
}

export const findLicence = (allLicences, feature) => {
  return allLicences.find(licence => licence.id === feature)
}

export const isLicenceAvailableToAllocateToUser = (user, feature, allLicences, allUsers) => {
  const masterLicence = findLicence(allLicences, features.MASTER_LICENCE)
  const requestedLicence = findLicence(allLicences, feature)

  const isPod = feature === features.POD_LICENCE

  const isMasterLicenceAvailable = !isNil(masterLicence) &&
      (isLicenceAllocatedToUser(user, masterLicence) ||
          (getAllocatedUsersForLicence(masterLicence, allUsers).length < masterLicence.qty))

  const isRequestedLicenceAvailable = !isNil(requestedLicence) &&
      (isLicenceAllocatedToUser(user, requestedLicence) ||
          (getAllocatedUsersForLicence(requestedLicence, allUsers).length < requestedLicence.qty))

  return (isPod || isMasterLicenceAvailable) && isRequestedLicenceAvailable
}

export const isLicenceAvailableToAllocate = (licence, users) => {
  return getAllocatedUsersForLicence(licence, users).length < licence.qty
}

export const addLicencesToUser = (user, licences) => {
  const licencesToAdd = licences.filter(licence => !isLicenceAllocatedToUser(user, licence))

  return [
    ...user.activeLicences,
    ...licencesToAdd.map(licence => ({id: licence.id})),
  ]
}

export const addLicenceToUser = (user, licence) => {
  if (isLicenceAllocatedToUser(user, licence))
    return user.activeLicences

  return [
    ...user.activeLicences,
    {id: licence.id},
  ]
}

export const addAccountPackageToUserPackages = (user, accountPackage) => {
  if (isPackageAllocatedToUser(user, accountPackage))
    return user.activePackages

  return [
    ...user.activePackages,
    {
      packageId: get(accountPackage, 'packageId'),
      partCode: get(accountPackage, 'partCode'),
      userId: user.id,
    },
  ]
}

export const removeLicenceFromUser = (user, licence) => {
  return getUserLicences(user).filter(userLicence => userLicence.id !== licence.id)
}

export const removePackageFromActivePackages = (user, pkg) => {
  const packageToRemoveId = pkg.packageId || get(pkg, 'id', null)

  return getUserPackages(user).filter(userPackage => {
    const userPackageId = userPackage.packageId || get(userPackage, 'id', null)
    return !isNil(userPackageId) && !isNil(packageToRemoveId) && userPackageId !== packageToRemoveId
  })
}

// Note: this is a basic test, the server can do a more rigorous test
export const isEmailValid = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export const contactMatchesSearch = (contact, search) => {
  return contact.email &&
      contact.email.toLowerCase().indexOf(search.toLowerCase()) >= 0
}

export const getNewUserOptions = (search, contacts, users) => {
  const possibleContacts = sortContacts(getContactsThatAreNotUsers(contacts, users))

  const options = possibleContacts
      .filter(contact => contactMatchesSearch(contact, search))
      .map(contact => ({
        value: contact.id,
        label: contact.email,
        disabled: !canContactBeAddedAsUser(contact),
        contact,
      }))

  if (search && search.length > 0) {
    const isExact = contacts.some(contact => contact.email === search)
    const isOnlyOption = isEmpty(options)

    if (isExact) {
      if (isOnlyOption) options.push({value: search, label: 'This person is already a user', disabled: true})
    } else {
      const emailIsValid = isEmailValid(search)
      if (emailIsValid || isOnlyOption) {
        const label = emailIsValid ? search : 'Enter a valid email address'
        options.push({value: search, label, disabled: !emailIsValid})
      }
    }
  }

  return options
}

export const setMainPackage = (user, newMainFeaturePackage, featurePackages, accountPackages) => {
  const currentMainAccountPackage = getSelectedMainAccountPackage(featurePackages, accountPackages)
  const otherAccountPackages = getNonMainAccountPackages(featurePackages, accountPackages)

  const newAccountPackages = [
    createAccountPackage(newMainFeaturePackage, currentMainAccountPackage),
    ...otherAccountPackages,
  ]

  return disableNonEntitledAdditionalPackages(user, featurePackages, newAccountPackages)
}

export const getMainPackageQuantity = (featurePackages, accountPackages) => {
  const mainAccountPackage = getSelectedMainAccountPackage(featurePackages, accountPackages)
  return mainAccountPackage ? mainAccountPackage.quantity : 0
}

export const getTrialPackageQuantity = (featurePackages, accountPackages) => {
  const trialAccountPackage = getSelectedTrialAccountPackage(featurePackages, accountPackages)
  return trialAccountPackage ? trialAccountPackage.quantity : 0
}

export const setMainPackageQuantity = (quantity, featurePackages, accountPackages) => {
  const currentMainAccountPackage = getSelectedMainAccountPackage(featurePackages, accountPackages)
  const otherAccountPackages = getNonMainAccountPackages(featurePackages, accountPackages)

  return [
    updateAccountPackageQuantity(currentMainAccountPackage, quantity),
    ...otherAccountPackages.map(accountPackage =>
        capAccountPackageQuantity(accountPackage, featurePackages, quantity)),
  ]
}

export const setTrialPackageQuantity = (
    quantity,
    user,
    featurePackages,
    accountPackages,
    filteredAccountPackages
) => {
  const currentSelectedAccountPackage = getSelectedTrialAccountPackage(featurePackages, filteredAccountPackages)
  const otherAccountPackages =
      getNonTrialAccountPackages(featurePackages, accountPackages, filteredAccountPackages)

  return [
    updateAccountPackageQuantity(currentSelectedAccountPackage, quantity),
    ...otherAccountPackages.map(accountPackage =>
        capAccountPackageQuantity(accountPackage, featurePackages, quantity)),
  ]
}

export const setAdditionalPackageQuantity = (quantity, featurePackage, accountPackages, featurePackages) => {
  const newAccountPackages = accountPackages.filter(accountPackage =>
      accountPackage.packageId !== featurePackage.id)

  if (quantity > 0) {
    let targetAccountPackage = findAccountPackageForFeaturePackage(accountPackages, featurePackage.id)
    if (isNil(targetAccountPackage)) targetAccountPackage = createAccountPackage(featurePackage)
    newAccountPackages.push(updateAccountPackageQuantity(targetAccountPackage, quantity))
  }

  if (isDeliverySchedulingPackage(featurePackage) && quantity < 1) {
    return getAccountPackagesWithoutPOD(newAccountPackages, featurePackages)
  }

  return newAccountPackages
}

export const getAccountPackagesWithoutPOD = (accountPackages, featurePackages) => {
  const podPackageId = findPackageIdByPartCode(featurePackages, partCodes.pod)
  return accountPackages.filter(accountPackage => accountPackage.packageId !== podPackageId)
}

export const haveUsersBeenAdded = (users) => {
  return users.some(newUser => newUser.isNew)
}

export const updatePackagesWithPartCodes = (accountPackages, featurePackages) => {
  return accountPackages.map(accountPackage => {
    const featurePackage = featurePackages.find(featurePackage => featurePackage.id === accountPackage.packageId)
    return {
      ...accountPackage,
      partCode: get(featurePackage, 'partCode', ''),
    }
  })
}

export const getNonIncludedFeaturePackages = (featurePackages, accountPackages) => {
  return featurePackages.filter(featurePackage => {
    return !accountPackages.some(accountPackage => accountPackage.packageId === featurePackage.id)
  })
}

export const getNonIncludedAccountPackage = (featurePackage) => {
  return {
    packageId: featurePackage.id,
    quantity: 0,
  }
}

export const managePackagesAndLicencesForUsers = (users, accountPackages, licences, featurePackages) => {
  let updatedUsers = [...users]

  const mainPackage = getSelectedMainAccountPackage(featurePackages, accountPackages)
  updatedUsers = manageMainPackageForUsers(updatedUsers, mainPackage)

  accountPackages.forEach(accountPackage => {
    updatedUsers = managePackageForUsers(updatedUsers, accountPackage)
  })

  const nonIncludedFeaturePackages = getNonIncludedFeaturePackages(featurePackages, accountPackages)
  nonIncludedFeaturePackages.forEach(featurePackage => {
    updatedUsers = managePackageForUsers(updatedUsers, getNonIncludedAccountPackage(featurePackage))
  })

  licences.forEach(licence => {
    updatedUsers = manageLicenceForUsers(updatedUsers, licence)
  })

  return applyDefaultLicences(updatedUsers, licences)
}

export const manageMainPackageForUsers = (users, newMainAccountPackage) => {
  return users.map(user => {
    return userHasMainPackage(user)
        ? {
          ...user,
          activePackages: addAccountPackageToUserPackages(user, newMainAccountPackage),
        }
        : user
  })
}

export const managePackageForUsers = (users, accountPackage) => {
  const usersUsingPackage = users.filter(user => isPackageAllocatedToUser(user, accountPackage))

  if (usersUsingPackage.length > accountPackage.quantity) {
    return capPackageForUsers(users, usersUsingPackage, accountPackage)
  }

  if (usersUsingPackage.length < accountPackage.quantity) {
    return reinstatePackageForUsers(users, usersUsingPackage, accountPackage)
  }

  return users
}

export const capPackageForUsers = (users, usersWithPackage, accountPackage) => {
  const usersToRemove = usersWithPackage.slice(accountPackage.quantity).map(user => user.id)

  return users.map(user => {
    return usersToRemove.includes(user.id)
        ? {
          ...user,
          activePackages: removePackageFromActivePackages(user, accountPackage),
          previousPackages: {
            ...user.previousPackages,
            [accountPackage.packageId]: true,
          },
        }
        : user
  })
}

export const reinstatePackageForUsers = (users, usersWithPackage, accountPackage) => {

  const usersWithPreviousPackage = users.filter(user =>
      !isPackageAllocatedToUser(user, accountPackage) &&
      !isNil(user.previousPackages) &&
      user.previousPackages[accountPackage.packageId]
  )

  const packagesToAdd = accountPackage.quantity - usersWithPackage.length

  const usersToAdd = usersWithPreviousPackage.length > packagesToAdd
      ? usersWithPreviousPackage.slice(0, packagesToAdd)
      : usersWithPreviousPackage

  const userIdsToAdd = usersToAdd.map(user => user.id)

  return users.map(user => {
    return userIdsToAdd.includes(user.id)
        ? {
          ...user,
          activePackages: addAccountPackageToUserPackages(user, accountPackage),
        }
        : user
  })
}

export const manageLicenceForUsers = (users, licence) => {
  const usersUsingLicence = users.filter(user => isLicenceAllocatedToUser(user, licence))

  if (usersUsingLicence.length > licence.qty)
    return capLicenceForUsers(users, usersUsingLicence, licence)

  if (usersUsingLicence.length < licence.qty)
    return reinstateLicenceForUsers(users, usersUsingLicence, licence)

  return users
}

export const capLicenceForUsers = (users, usersWithLicence, licence) => {
  const usersToRemove = usersWithLicence.slice(licence.qty).map(user => user.id)
  return users.map(user => {
    return usersToRemove.includes(user.id)
        ? {
          ...user,
          activeLicences: removeLicenceFromUser(user, licence),
          previousLicences: {
            ...user.previousLicences,
            [licence.id]: true,
          },
        }
        : user
  })
}

export const reinstateLicenceForUsers = (users, usersWithLicence, licence) => {
  const usersWithPreviousLicence = users.filter(user => {
    return (!isLicenceAllocatedToUser(user, licence)
        && user.previousLicences && user.previousLicences[licence.id])
  })

  const licencesToAdd = licence.qty - usersWithLicence.length

  const usersToAdd = usersWithPreviousLicence.length > licencesToAdd
      ? usersWithPreviousLicence.slice(0, licencesToAdd)
      : usersWithPreviousLicence

  const userIdsToAdd = usersToAdd.map(user => user.id)

  return users.map(user => {
    return userIdsToAdd.includes(user.id)
        ? {
          ...user,
          activeLicences: addLicenceToUser(user, licence),
        }
        : user
  })
}

export const getAvailableFeatureCount = (users, licence, allocated) => {
  const allocatedUsers = getAllocatedUsersForLicence(licence, users).length
  const allocatedCount = licence.qty - allocatedUsers
  const count = allocated ? (allocatedCount - 1) : allocatedCount
  return Math.max(0, count)
}

export const getSelectedFeatureIds = (selectedFeatureIds, id) => {
  return selectedFeatureIds.includes(id)
      ? selectedFeatureIds.filter((selectedFeatureId) => {
        return selectedFeatureId !== id
      })
      : [...selectedFeatureIds, id]
}

export const isLicenceAllocated = (selectedPackages, id, selectedFeatureIds) => {
  return isLicenceLinkedToPackageWithOnlyOneLicence(selectedPackages, id) ? true : selectedFeatureIds.includes(id)
}

export const isLicenceLinkedToPackageWithOnlyOneLicence = (selectedPackages, id) => {
  const filteredSelectedPackages = selectedPackages.filter(selectedPackage =>
      selectedPackage.licences.length === 1)

  const count = filteredSelectedPackages.reduce((licenceCount, selectedPackage) => {
    const matchingLicences = selectedPackage.licences.filter(licenceId => licenceId === id)
    return licenceCount + matchingLicences.length
  }, 0)
  return count === 1
}

export const isLicenceLinkedToAnyActivePackage = (packages, id, user) => {
  const activePackages = getUserPackages(user)
  const selectedPackages = packages
      .filter(userPackage => activePackages
          .some(activePackage =>
              activePackage.packageId === userPackage.id || activePackage.id === userPackage.id))
  const filteredSelectedPackages =
      selectedPackages
          .filter(selectedPackage => selectedPackage.licences
              .some(licence => licence === id || licence.licenceId === id))
  return filteredSelectedPackages.length > 0
}

export const isLicenceLinkedToAnySelectedPackage = (selectedPackages, id) => {
  const filteredSelectedPackages =
      selectedPackages
          .filter(selectedPackage => selectedPackage.licences
              .some(licence => licence === id || licence.licenceId === id))
  return filteredSelectedPackages.length > 0
}

export const getSelectedPackages = (
    selectedPackages,
    field,
    packages,
    allocatableLicences,
    selectedFeatureIds
) => {
  let selectedUserPackages = selectedPackages
      .filter(selectedPackage => selectedPackage.id === field.id)
      .length === 0
      ? [
        ...selectedPackages,
        {
          id: get(field, 'id', null),
          partCode: get(field, 'partCode', null),
          licences: getLicencesForPackage(packages, field.id, allocatableLicences),
        },
      ]
      : selectedPackages.filter(selectedPackage => selectedPackage.id !== field.id)

  if (field.partCode === partCodes.premium) {
    selectedUserPackages = field.value
        ? selectedUserPackages.filter(item => !packagesShownWithLicences.includes(item.partCode))
        : selectedUserPackages
  }

  const selectedLicenceIds =
      getSelectedLicencesForPackage(selectedUserPackages, selectedFeatureIds, packages, field)
  return {
    selectedUserPackages,
    selectedLicenceIds,
  }
}

export const getLicencesForPackage = (packages, id, allocatableLicences) => {
  const userPackage = packages.find(packageDetails => get(packageDetails, 'id', null) === id)
  const packageLicences = get(userPackage, 'licences', [])
  return getOptionalLicences(packageLicences, allocatableLicences)
}

export const getOptionalLicences = (packageLicences, allocatableLicences) => {
  return packageLicences
      .filter(packageLicence => allocatableLicences
          .some(allocatableLicence => allocatableLicence.id === packageLicence.licenceId
              && allocatableLicence.isMaster === false))
      .map(licence => licence.licenceId)
}

export const getSelectedLicencesForPackage = (selectedPackages, selectedFeatureIds, userPackages, field) => {
  const userPackage = userPackages.filter(userPackage => userPackage.id === field.id)
  const licences = userPackage[0].licences
  const licenceMap = new Map(licences.map(licence => [licence.licenceId, licence]))
  const sortedIds = licenceOrder.filter(id => licenceMap.has(id))
  const isPackageChecked = !field.value
  return isPackageChecked ?
      [...selectedFeatureIds, ...sortedIds]
      : selectedFeatureIds
          .filter(id => isLicenceLinkedToAnySelectedPackage(selectedPackages, id) || !sortedIds.includes(id))
}

export const isPackageCheckboxRendered = (user, userPackage) => {
  const userPackages = getUserPackages(user)

  if (user.isExternalContact && userPackage.partCode !== partCodes.pod) return false

  if (isEmpty(userPackages)) return true

  switch (userPackage.partCode) {
    case partCodes.premium:
      return !(userHasPackage(user, partCodes.management) || userHasPackage(user, partCodes.shopFloor))
    case partCodes.shopFloor:
      return !(userHasPackage(user, partCodes.premium) || userHasPackage(user, partCodes.management))
    case partCodes.management:
      return !(userHasPackage(user, partCodes.premium) || userHasPackage(user, partCodes.shopFloor))
    default:
      return true
  }
}

export const isPackageCheckboxEnabled = (user, userPackage, userLoggedIn, isReadOnly) => {
  if (isReadOnly) return false
  if (!userHasPermission(userLoggedIn, permissions.MANAGE_LOGINS)) return false
  if (isPackageAllocatedToUser(user, userPackage)) return true

  const total = get(userPackage, 'qty', 0) * getActiveUserLicenceQty(userPackage)
  const used = get(userPackage, 'used', 0)
  return used < total && total > 0
}

export const getAllocatableLicences = (licences) => {
  return licences.filter(licence => featureIsAllocatable(licence.licenceId))
}

export const getUpdatedUserLicencesForPackageAllocation = (user, userPackage, packages) => {
  return isPackageAllocatedToUser(user, userPackage)
      ? removeUserLicences(user, userPackage, packages)
      : addUserLicences(user, userPackage)
}

export const removeUserLicences = (user, userPackage, packages) => {

  const userLicences = getUserLicences(user)
  const packageLicences = get(userPackage, 'licences', [])

  const userLicenceIds = getLicenceIds(userLicences)
  const packageLicenceIds = getLicenceIds(packageLicences)

  const updatedUser = {
    ...user,
    activePackages: removeUserPackage(user, userPackage),
  }

  const filteredLicenceIds = userLicenceIds.filter(licenceId =>
      isLicenceLinkedToAnyActivePackage(packages, licenceId, updatedUser)
      || !packageLicenceIds.includes(licenceId))

  return userLicences.filter(licence =>
      filteredLicenceIds.includes(licence.licenceId) || filteredLicenceIds.includes(licence.id))
}

export const addUserLicences = (user, userPackage) => {
  const packageLicences = get(userPackage, 'licences', [])
  const filteredPackageLicences = getAllocatableLicences(packageLicences)
  const userLicences = getUserLicences(user)
  return filteredPackageLicences.concat(userLicences)
}

export const addUserPackage = (user, newPackage) => {
  return [
    ...getUserPackages(user),
    {
      packageId: newPackage.id,
      partCode: newPackage.partCode,
      userId: user.id,
    },
  ]
}

export const removeUserPackage = (user, packageToRemove) => {
  const userPackages = getUserPackages(user)

  const userPackage = userPackages.find(userPackage => userPackage.packageId === packageToRemove.packageId)
  const userPackagePartCode = get(userPackage, 'partCode')
  const packageToRemovePartCode = packageToRemove.partCode || userPackagePartCode

  const packageToRemoveId = packageToRemove.id || get(packageToRemove, 'packageId', 0)

  if (packageToRemovePartCode === partCodes.premium) {
    return getFilteredPremiumPackages(userPackages, packageToRemoveId)
  }

  return userPackages.filter((userPackage => {
    return userPackage.packageId
        ? userPackage.packageId !== packageToRemoveId
        : userPackage.id !== packageToRemoveId
  }))
}

export const getFilteredPremiumPackages = (userPackages, packageToRemoveId) => {
  return userPackages.filter(userPackage => {
    if (packagesShownWithLicences.includes(userPackage.partCode)) {
      return false
    }
    return userPackage.packageId
        ? userPackage.packageId !== packageToRemoveId
        : userPackage.id !== packageToRemoveId
  })
}

export const getUpdatedUserPackagesForPackageAllocation = (user, userPackage) => {
  return isPackageAllocatedToUser(user, userPackage)
      ? removeUserPackage(user, userPackage)
      : addUserPackage(user, userPackage)
}

export const addUserLicence = (user, newLicence) => {
  return [...getUserLicences(user), newLicence]
}

export const removeUserLicence = (user, licenceToRemove) => {
  return getUserLicences(user).filter((userLicence => userLicence.id !== licenceToRemove.licenceId))
}

export const removeChangePlanUserLicence = (user, licenceToRemove) => {
  return getUserLicences(user).filter((userLicence => {
    return userLicence.licenceId
        ? userLicence.licenceId !== licenceToRemove.licenceId
        : userLicence.id !== licenceToRemove.licenceId
  }))
}

export const getUpdatedUserLicencesForLicenceAllocation = (user, licence) => {
  return isLicenceAllocatedToUser(user, licence)
      ? removeUserLicence(user, licence)
      : addUserLicence(user, licence)
}

export const getUpdatedLicencesForChangePlanLicenceAllocation = (user, licence) => {
  return isLicenceCheckboxChecked(user, licence)
      ? removeChangePlanUserLicence(user, licence)
      : addUserLicence(user, licence)
}

export const getLicenceIds = (licences) => {
  return licences.map(licence => licence.licenceId || licence.id)
}

export const getPackageIds = (userPackages) => {
  return userPackages.map(userPackage => userPackage.packageId || userPackage.id)
}

export const isLicenceCheckboxEnabled = (user, licence, userLoggedIn, isReadOnly) => {
  return !isReadOnly && userHasPermission(userLoggedIn, permissions.MANAGE_LOGINS)
}

export const userHasPackage = (user, partCode) => {
  const userPackages = getUserPackages(user)
  return userPackages.some(userPackage => userPackage.partCode === partCode)
}

export const userHasMainPackage = (user) => {
  const userPackages = getUserPackages(user)
  return userPackages.some(userPackage => mainPackages.includes(userPackage.partCode))
}

export const isLicenceCheckboxRendered = (user, licence, userPackages) => {
  const licenceId = get(licence, 'licenceId', null)
  const packagesLinkedToLicence = getPackagesLinkedToLicence(userPackages, licence.licenceId)
  const userActivePackage = get(user, 'activePackages', [])
  const activePackagesPartCodeList = userActivePackage.map(selectedPackage => selectedPackage.partCode)

  const isLicenceLinkedToSalesPlusOrPurchasingPackage =
      licencesInSinglePackage.includes(licenceId)
      && activePackagesPartCodeList.includes(partCodes.premium)

  return isLicenceLinkedToSalesPlusOrPurchasingPackage
      || packagesLinkedToLicence.some(item => activePackagesPartCodeList.includes(item.partCode))
}


export const getPackagesShownWithPackages = (packages) => {
  return packages.filter(userPackage => !packagesShownWithLicences.includes(userPackage.partCode))
}

export const getPackagesShownWithLicences = (packages) => {
  return packages.filter(userPackage => {
    return packagesShownWithLicences.includes(userPackage.partCode) && userPackage.qty > 0
  })
}

export const getNonLitePackages = (packages) => {
  return packages.filter(userPackage => userPackage.partCode !== partCodes.lite)
}

export const getSecondaryPackages = (mainPackages, packages) => {
  const isLitePackage = mainPackages.some(userPackage => userPackage.partCode === partCodes.lite)
  return !isLitePackage ? getPackagesShownWithLicences(Object.values(packages)) : []
}

export const getSelectedPlanPackages = (allPackages, mainAccountPackage) => {
  const litePackage = getLitePackage(allPackages)
  const liteId = get(litePackage, 'id', null)
  const mainPackageId = get(mainAccountPackage, 'packageId', null)

  return !isNil(mainPackageId) && mainPackageId === liteId
      ? [litePackage]
      : getNonLitePackages(getPackagesShownWithPackages(allPackages))
}

export const getLitePackage = (packages) => {
  return packages.find(userPackage => userPackage.partCode === partCodes.lite)
}

export const getFilteredAvailableLicences = (licences) => {
  return getAvailableLicences(licences).filter(licence => {
    return !nonPremiumLicences.includes(licence.id)
  })
}

export const isLicenceQtyRendered = (licence) => {
  const licenceId = get(licence, 'licenceId', 0)
  return licencesInSinglePackage.includes(licenceId)
}

export const nonPremiumLicences = [
  features.POD_LICENCE,
  features.MANAGEMENT_LICENCE,
  features.PURCHASING_LICENCE,
  features.SALES_PLUS_LICENCE,
  features.DELIVERY_SCHEDULING,
  features.HUBSPOT_SYNC,
  features.MOBILE_APP_LOGIN,
  features.SAGE_50,
]

export const licencesInSinglePackage = [
  features.PURCHASING_LICENCE,
  features.SALES_PLUS_LICENCE,
  features.DELIVERY_SCHEDULING,
  features.SAGE_50,
]

export const getLicenceColumns = (licences, packages) => {
  const columns = [
    ...licences.map(licence => createColumnForLicence(licence)),
    ...packages.map(userPackage => createColumnForPackage(userPackage)),
  ]
  const indexedColumns = applySortedIndex(columns, licenceOrder, 'licenceId')
  return sortBy(indexedColumns, 'index')
}

export const applySortedIndex = (array, sortedArray, keyProp) => {
  return array.map(item => {
    return {
      ...item,
      index: sortedArray.indexOf(item[keyProp]),
    }
  })
}

export const createColumnForLicence = (licence) => {
  return {
    name: licence.name,
    licenceId: licence.id,
    packageId: null,
    qty: licence.qty,
    used: licence.used,
  }
}

export const createColumnForPackage = (userPackage) => {
  return {
    name: userPackage.name,
    licenceId: get(userPackage, 'licences[0].licenceId'),
    packageId: userPackage.id,
    qty: userPackage.qty,
    used: userPackage.used,
  }
}

export const isPackageCheckBoxEnabledForAddUser = (selectedPackages, userPackage) => {
  const partCode = get(userPackage, 'partCode', null)
  switch (partCode) {
    case partCodes.premium:
      return selectedPackages.some(selectedPackage =>
          selectedPackage.partCode === partCodes.management
          || selectedPackage.partCode === partCodes.shopFloor
      )
    case partCodes.shopFloor:
      return selectedPackages.some(selectedPackage =>
          selectedPackage.partCode === partCodes.premium
          || selectedPackage.partCode === partCodes.management
      )
    case partCodes.management:
      return selectedPackages.some(selectedPackage =>
          selectedPackage.partCode === partCodes.premium
          || selectedPackage.partCode === partCodes.shopFloor
      )
    case partCodes.salesPlus:
      return !selectedPackages.some(selectedPackage =>
          selectedPackage.partCode === partCodes.premium)
    case partCodes.purchasing:
      return !selectedPackages.some(selectedPackage =>
          selectedPackage.partCode === partCodes.premium)
    default:
      return false
  }
}

export const isAddUserWarningVisibleForPackageAllocation = (packages) => {
  return getOrderedPackages(packages).every(userPackage =>
      userPackage.used === userPackage.qty * getActiveUserLicenceQty(userPackage))
}

export const isAddExternalUserWarningVisibleForPackageAllocation = (userPackages) => {
  const userPackage = userPackages.find(userPackage => userPackage.partCode === partCodes.pod)
  return userPackage.used === userPackage.qty
}

export const getPackagesLinkedToLicence = (packages, licenceId) => {
  return packages.filter(userPackage => userPackage.licences.some(licence => licence.licenceId === licenceId))
}

export const getActiveUserLicenceQty = (userPackage) => {
  const activeUserLicence = get(userPackage, 'licences', [])
      .find(licence => licence.licenceId === features.MASTER_LICENCE)
  return get(activeUserLicence, 'quantity', 1)
}

export const updateContactsLicencesAndPackages = (contacts, userId, licenceIds, packageIds, allPackages) => {
  const contact = Object.values(contacts).find(contact => contact.userId === userId)
  if (isNil(contact))
    return contacts

  return {
    ...contacts,
    [contact.id]: updateContactLicencesAndPackages(contact, licenceIds, packageIds, allPackages),
  }
}

export const updateContactLicencesAndPackages = (contact, licenceIds, packageIds, allPackages) => {
  return {
    ...contact,
    activeLicences: licenceIds.map(licenceId => ({
      id: licenceId,
    })),
    activePackages: isNil(packageIds)
        ? contact.activePackages
        : packageIds.map(packageId => ({
          userId: contact.userId,
          packageId: packageId,
          partCode: findPackagePartCode(allPackages, packageId),
        })),
  }
}

export const getAllContacts = (internalContacts, externalContacts) => {
  return [
    ...Object.values(internalContacts),
    ...Object.values(externalContacts),
  ]
}

export const getDirtyContacts = (contacts) => {
  return contacts.filter(contact => contact.dirty === true)
}

export const updateLicences = (licences, allContacts) => {
  const updatedLicences = {}
  Object.keys(licences).forEach(licenceId => {
    const licence = licences[licenceId]
    updatedLicences[licenceId] = updateLicence(licence, allContacts)
  })
  return updatedLicences
}

export const updateLicence = (licence, allContacts) => {
  const used = getUsedLicences(licence, allContacts)
  return {
    ...licence,
    used,
    remaining: licence.qty - used,
  }
}

export const getUsedLicences = (licence, allContacts) => {
  return allContacts
      .filter(contact => isLicenceAllocatedToUser(contact, licence))
      .length
}

export const updatePackages = (packages, allContacts) => {
  const updatedPackages = {}
  Object.keys(packages).forEach(packageId => {
    const pkg = packages[packageId]
    updatedPackages[packageId] = updatePackage(pkg, allContacts)
  })
  return updatedPackages
}

export const updatePackage = (pkg, allContacts) => {
  const used = getUsedPackages(pkg, allContacts)
  return {
    ...pkg,
    used,
  }
}

export const getUsedPackages = (pkg, allContacts) => {
  return allContacts
      .filter(contact => isPackageAllocatedToUser(contact, pkg))
      .length
}

export const getAnyContactError = (contacts) => {
  const contactWithError = contacts.find(contact => !isNil(contact.error))
  return get(contactWithError, 'error', null)
}

export const isCurrentUserSelected = (currentUser, selectedUser) => {
  const currentUserId = get(currentUser, 'id', null)
  const selectedUserId = get(selectedUser, 'userId', null)

  return !isNil(currentUserId) && !isNil(selectedUserId) && currentUserId === selectedUserId
}
